import React, { useCallback, useEffect } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import { useSelector } from 'react-redux'
import { Icon, Container, Segment } from 'semantic-ui-react'

import { getExternalLinks } from 'stores/Links/Select'
import useMediaQuery from 'hooks/useMediaQuery'
import { useTheme } from 'contexts/ThemeProvider'

import { localization } from './localization'
import themeStyles from './styles'

import MobileSlider from 'components/Tiles/WellnessResources/MobileSlider'
import withLocalization from '@web/helpers/localizationUtils'
import Image from 'next/image'

const SLIDE_GAP = 0

const WellnessResourcesComponent = ({ t }) => {
  const { isMobile, isTablet, isSmallMonitor } = useMediaQuery()
  const { theme } = useTheme()
  const VISIBLE_SLIDES = isTablet ? 2 : isSmallMonitor ? 3 : 4
  const styles = themeStyles(theme)
  const links = useSelector(getExternalLinks)
  const moreFromChocLinks = links?.MorefromCHOC

  const RESOURCES_ITEMS = [
    {
      id: '1',
      image: moreFromChocLinks?.UFD_Parenting_Content_resourceUrl,
      title: t('resource1_title'),
      link: moreFromChocLinks?.UFD_Parenting_Content
    },
    {
      id: '2',
      image: moreFromChocLinks?.UFD_CHOC_News_resourceUrl,
      title: t('resource2_title'),
      link: moreFromChocLinks?.UFD_CHOC_News
    },
    {
      id: '3',
      image: moreFromChocLinks?.UFD_Visting_CHOC_resourceUrl,
      title: t('resource3_title'),
      link: moreFromChocLinks?.UFD_Visting_CHOC
    },
    {
      id: '4',
      image: moreFromChocLinks?.UFD_Patients_Families_resourceUrl,
      title: t('resource4_title'),
      link: moreFromChocLinks?.UFD_Patients_Families
    },
    {
      id: '5',
      image: moreFromChocLinks?.UFD_Ways_to_Help_resourceUrl,
      title: t('resource5_title'),
      link: moreFromChocLinks?.UFD_Ways_to_Help
    },
    {
      id: '6',
      image: moreFromChocLinks?.UFD_Stay_Connected_resourceUrl,
      title: t('resource6_title'),
      link: moreFromChocLinks?.UFD_Stay_Connected
    }
  ]

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    loop: false,
    skipSnaps: false
  })

  const scrollPrev = useCallback(() => {
    emblaApi?.scrollPrev()
  }, [emblaApi])
  const scrollNext = useCallback(() => {
    emblaApi?.scrollNext()
  }, [emblaApi])

  const [prevBtnDisabled, setPrevBtnDisabled] = React.useState(true)
  const [nextBtnDisabled, setNextBtnDisabled] = React.useState(true)

  const updateButtonState = useCallback(() => {
    if (!emblaApi) {
      return
    }
    setPrevBtnDisabled(!emblaApi.canScrollPrev())
    setNextBtnDisabled(!emblaApi.canScrollNext())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) {
      return
    }
    updateButtonState()
    emblaApi.on('select', updateButtonState)
    return () => {
      emblaApi.off('select', updateButtonState)
    }
  }, [emblaApi, updateButtonState])

  return (
    <Segment style={styles.container} className='wellnessResources'>
      <Container>
        <div style={styles.wellnessTitlesContainer}>
          <div style={styles.title}>{t('links_area_title')}</div>
        </div>
        {!isMobile && (
          <div style={styles.carousel}>
            <div style={styles.linksSliderArea}>
              <button
                type='button'
                aria-label='previous'
                className='wellnessResourcesNavButton'
                style={styles.sliderButton}
                onClick={scrollPrev}
                disabled={prevBtnDisabled}
              >
                {prevBtnDisabled ? (
                  <Icon style={styles.iconAngleDisabled} name='angle left' />
                ) : (
                  <Icon style={styles.iconAngle} name='angle left' />
                )}
              </button>
              <div
                style={{ overflow: 'hidden', flex: 1, minWidth: 0 }}
                ref={emblaRef}
              >
                <div style={{ display: 'flex', marginLeft: -SLIDE_GAP }}>
                  {RESOURCES_ITEMS.map(item => (
                    <div
                      key={item.id}
                      className='wellnessResourcesSlide'
                      style={{
                        ...styles.slide,
                        flex: `0 0 ${100 / VISIBLE_SLIDES}%`
                      }}
                    >
                      <div style={styles.slideImage}>
                        {item.image ? (
                          <Image
                            src={item.image}
                            alt={item.title}
                            fill
                            style={{ objectFit: 'cover' }}
                          />
                        ) : (
                          <div
                            style={{
                              width: '100%',
                              height: '100%',
                              background: 'var(--approxGray, #eee)'
                            }}
                            aria-hidden
                          />
                        )}
                      </div>
                      <div style={styles.slideInfo}>
                        <div style={styles.slideText}>
                          <span style={styles.slideTitle}>{item.title}</span>
                          <span style={styles.slideDescription}>
                            {item.description}
                          </span>
                        </div>
                        <a
                          href={item.link}
                          target='_blank'
                          rel='noreferrer'
                          style={styles.slideLink}
                        >
                          {t('read')}
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <button
                type='button'
                aria-label='next'
                className='wellnessResourcesNavButton'
                style={styles.sliderButton}
                onClick={scrollNext}
                disabled={nextBtnDisabled}
              >
                {nextBtnDisabled ? (
                  <Icon style={styles.iconAngleDisabled} name='angle right' />
                ) : (
                  <Icon style={styles.iconAngle} name='angle right' />
                )}
              </button>
            </div>
          </div>
        )}
        {isMobile && <MobileSlider items={RESOURCES_ITEMS} />}
      </Container>
    </Segment>
  )
}

export const WellnessResources = withLocalization(
  WellnessResourcesComponent,
  localization
)
